'use client'

import { useId } from 'react'
import { cn } from '../../../lib/cn'

export interface GradientBlurProps {
  className?: string
}

/**
 * GradientBlur - Ambient blurred blobs background
 *
 * Creates a soft, modern gradient effect with 2-3 blurred circles
 * positioned around the edges. Uses theme's primary color for
 * universal compatibility across all themes.
 *
 * Fixed settings (not configurable):
 * - Light mode: 4-10% opacity blobs
 * - Dark mode: 5-12% opacity blobs
 * - Uses --primary color from theme
 *
 * @example
 * ```tsx
 * <GradientBlur />
 * ```
 */
export function GradientBlur({ className }: GradientBlurProps = {}) {
  const id = useId()
  const filterId = `gradient-blur-${id}`

  return (
    <div
      className={cn('pointer-events-none absolute inset-0 z-0 overflow-hidden', className)}
    >
      {/* Light mode - blur 100 */}
      <svg
        className="absolute inset-0 h-full w-full dark:hidden"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <filter
            id={`${filterId}-light`}
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
            filterUnits="objectBoundingBox"
          >
            <feGaussianBlur stdDeviation="100" result="blur" />
          </filter>
        </defs>

        <ellipse
          cx="15%"
          cy="10%"
          rx="25%"
          ry="30%"
          className="fill-primary opacity-[0.10]"
          filter={`url(#${filterId}-light)`}
        />
        <ellipse
          cx="85%"
          cy="20%"
          rx="30%"
          ry="25%"
          className="fill-primary opacity-[0.04]"
          filter={`url(#${filterId}-light)`}
        />
        <ellipse
          cx="60%"
          cy="90%"
          rx="35%"
          ry="30%"
          className="fill-primary opacity-[0.07]"
          filter={`url(#${filterId}-light)`}
        />
      </svg>

      {/* Dark mode - more blur, more subtle */}
      <svg
        className="absolute inset-0 hidden h-full w-full dark:block"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <filter
            id={`${filterId}-dark`}
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
            filterUnits="objectBoundingBox"
          >
            <feGaussianBlur stdDeviation="130" result="blur" />
          </filter>
        </defs>

        <ellipse
          cx="15%"
          cy="10%"
          rx="25%"
          ry="30%"
          className="fill-primary opacity-[0.12]"
          filter={`url(#${filterId}-dark)`}
        />
        <ellipse
          cx="85%"
          cy="20%"
          rx="30%"
          ry="25%"
          className="fill-primary opacity-[0.05]"
          filter={`url(#${filterId}-dark)`}
        />
        <ellipse
          cx="60%"
          cy="90%"
          rx="35%"
          ry="30%"
          className="fill-primary opacity-[0.08]"
          filter={`url(#${filterId}-dark)`}
        />
      </svg>
    </div>
  )
}
